/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { Target } from '@fluentui/react/lib/Callout';
import { Stack } from '@fluentui/react/lib/Stack';
import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { tokenToCharIndices } from 'src/core/utils/labelingUtils';
import { ReviewEndpointUtterancesUIStore } from 'src/luis/appShell/buildPane/reviewEndpointPage/ReviewEndpointUtterancesUIStore';
import { SuggestedUtteranceObservable } from 'src/luis/projects/utterances/utils/generateSuggestedUtterancesObservables';
import { isMLPrediction } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { LuisId } from 'src/luis/types/Resource';
import { Labeler, LabelerRef } from 'src/modules/labeling/components/Labeler';
import { Label } from 'src/modules/labeling/types/Label';
import { Selection } from 'src/modules/labeling/types/Selection';
import { INVALID_INDEX } from 'src/modules/labeling/utils/labelingReducer';

const messages = defineMessages({
    confirmPredictions: {
        id: 'utterance.suggestedUtteranceRow.confirmPredictions',
        defaultMessage: 'Confirm predictions'
    },
    saveUtterance: {
        id: 'utterance.suggestedUtteranceRow.saveUtterance',
        defaultMessage: 'Save utterance'
    },
    discardUtterance: {
        id: 'utterance.suggestedUtteranceRow.discardUtterance',
        defaultMessage: 'Discard utterance'
    },
    unConfirmedPredictions: {
        id: 'utterance.suggestedUtteranceRow.unConfirmedPredictions',
        defaultMessage: 'Note: You have unconfirmed predictions'
    }
});

export type SuggestedUtteranceRowProps = {
    utteranceObservable: SuggestedUtteranceObservable;
    reviewEndpointUtteranceUIStore: ReviewEndpointUtterancesUIStore;
    isRTL: boolean;
    onSubmit: (id: LuisId) => void;
    onDiscard: (id: LuisId) => void;
    onConfirmPredictions: (id: LuisId) => void;
    /**
     * User role to determine the actions.
     */
    userRole: string;
};

const getSelectedText = (text: string, tokenizedText: string[], startTokenIndex: number, endTokenIndex: number) => {
    const charIndices = tokenToCharIndices({
        text,
        tokenizedText,
        startTokenIndex,
        endTokenIndex
    });

    return text.substring(charIndices.startCharIndex, charIndices.endCharIndex + 1);
};

const { styleDiv } = getStylistV2('SuggestedUtteranceRow');

const LabelerContainer = styleDiv('LabelerContainer', {
    flex: 1
});

/**
 * Table row that contains the utterance and allows for labeling that utterance.
 */
export const SuggestedUtteranceRow = observer((props: SuggestedUtteranceRowProps) => {
    const { utteranceObservable, isRTL, reviewEndpointUtteranceUIStore, onConfirmPredictions, onDiscard, onSubmit, userRole } = props;
    const [isHovered, setIsHovered] = React.useState(false);
    const labelerRef = React.useRef<LabelerRef>();
    const { localize } = useLocalization();

    const { menuStore } = reviewEndpointUtteranceUIStore.labelingStore;
    const [utterance, { conflicts, labels }] = utteranceObservable.computed.get();
    const hasPredictions = labels.some(isMLPrediction);
    const submitTooltipContent = hasPredictions ? (
        <p>
            {localize(messages.saveUtterance)}
            <br />
            {localize(messages.unConfirmedPredictions)}
        </p>
    ) : (
        localize(messages.saveUtterance)
    );
    const isInFlightRequest = reviewEndpointUtteranceUIStore.inFlightUtteranceIds.has(utterance.id);

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const handleSelect = React.useCallback(
        (selection?: Selection, target?: Target) => {
            if (!selection || selection[0] === INVALID_INDEX) {
                return;
            }

            transaction(() => {
                const selectedText = getSelectedText(utterance.text, utterance.tokenizedText, selection[0], selection[1]);

                reviewEndpointUtteranceUIStore.labelingStore.setTokenSelectionMetadata({
                    selection: selection as Selection,
                    utteranceId: utterance.id,
                    selectedText
                });

                menuStore.setCancelSelectionCallBackCB(() => labelerRef.current.cancelSelection());

                if (reviewEndpointUtteranceUIStore.labelingStore.mode === 'menu') {
                    if (utterance.id !== menuStore.currentUtteranceId) {
                        menuStore.setCurrentUtteranceId(utterance.id);
                    }

                    menuStore.setTarget(target);
                }
            });
        },
        [reviewEndpointUtteranceUIStore, labelerRef, utterance, menuStore]
    );

    const handleShapeClick = React.useCallback(
        (label: Label, target: Target) => {
            transaction(() => {
                if (utterance.id !== menuStore.currentUtteranceId) {
                    menuStore.setCurrentUtteranceId(utterance.id);
                }

                reviewEndpointUtteranceUIStore.labelingStore.setTokenSelectionMetadata({
                    selection: [label.start, label.end],
                    utteranceId: utterance.id,
                    selectedText: getSelectedText(utterance.text, utterance.tokenizedText, label.start, label.end)
                });
                menuStore.setTarget(target);
                menuStore.setClickedOnLabel(label);
                menuStore.setCancelSelectionCallBackCB(() => labelerRef.current.cancelSelection());
            });
        },
        [reviewEndpointUtteranceUIStore, menuStore, utterance]
    );

    return (
        <Stack
            grow
            horizontal
            verticalAlign="center"
            verticalFill
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <LabelerContainer>
                <Labeler
                    ref={labelerRef}
                    config={{ disabled: !canWrite, isRTL }}
                    linesTokens={[utterance.tokenizedText]}
                    text={utterance.text}
                    labels={labels}
                    conflicts={conflicts}
                    onSelect={handleSelect}
                    onLabelResize={noop}
                    onShapeClick={handleShapeClick}
                    onTokenHover={noop}
                    onTokenHoverExit={noop}
                />
            </LabelerContainer>

            {canWrite && (
                <Stack style={{ opacity: isHovered ? 1 : 0 }} horizontal>
                    <LuisTooltip content={localize(messages.confirmPredictions)}>
                        <IconButton
                            data-automation-id="SuggestedRowAcceptPrediction"
                            disabled={isInFlightRequest || !hasPredictions}
                            iconProps={{ iconName: 'accept' }}
                            onClick={() => onConfirmPredictions(utterance.id)}
                        />
                    </LuisTooltip>
                    <LuisTooltip content={submitTooltipContent}>
                        <IconButton
                            data-automation-id="SuggestedRowSubmit"
                            disabled={isInFlightRequest}
                            iconProps={{ iconName: 'upload' }}
                            onClick={() => onSubmit(utterance.id)}
                        />
                    </LuisTooltip>
                    <LuisTooltip content={localize(messages.discardUtterance)}>
                        <IconButton
                            data-automation-id="SuggestedRowDiscard"
                            disabled={isInFlightRequest}
                            iconProps={{ iconName: 'cancel' }}
                            onClick={() => onDiscard(utterance.id)}
                        />
                    </LuisTooltip>
                </Stack>
            )}
        </Stack>
    );
});
